import { useState, useRef } from 'react'
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FloatingActionButton, Button, GlassPanel, ProtectedRoute, EntityCard, ProjectLayout, Icon, OnboardingOverlay } from './components/common'
import { EntityModal } from './components/EntityModal'
import { useAuth } from './context/AuthContext'
import { useCharacterOperations, useLocationOperations, useEventOperations, useShotOperations } from './hooks/useFirestore'
import { APP_LOGO_ICON_NAME, APP_LOGO_TEXT, BRANDING } from './config/branding'
import LoginView from './views/LoginView'
import LandingView from './views/LandingView'
import TermsView from './views/TermsView'
import PrivacyView from './views/PrivacyView'
import RefundsView from './views/RefundsView'
import CancellationView from './views/CancellationView'
import { PublicFooter } from './components/common/PublicFooter'
import ProjectsListView from './views/ProjectsListView'
import CharactersView from './views/CharactersView'
import LocationsView from './views/LocationsView'
import EventsView from './views/EventsView'
import ShotsView from './views/ShotsView'
import TimelineView from './views/TimelineView'
import SynopsisView from './views/SynopsisView'
import ImagesView from './views/ImagesView'
import ImportView from './views/ImportView'
import StoryboardView from './views/StoryboardView'
import PromptsView from './views/PromptsView'
import ProjectMembersView from './views/ProjectMembersView'
import TestingView from './views/TestingView'
import PaymentView from './views/PaymentView'
import './App.css'
import './views/LoginView.css'

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Design System Demo component to showcase the new components
const DesignSystemDemo = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalConfig, setModalConfig] = useState({
    entityType: 'character',
    mode: 'create',
    initialData: null,
  })
  const [originRect, setOriginRect] = useState(null)
  const fabRef = useRef(null)

  // Firestore operations for each entity type
  const characterOps = useCharacterOperations()
  const locationOps = useLocationOperations()
  const eventOps = useEventOperations()
  const shotOps = useShotOperations()

  const getOperationsForType = (type) => {
    switch (type) {
      case 'character': return characterOps
      case 'location': return locationOps
      case 'event': return eventOps
      case 'shot': return shotOps
      default: return characterOps
    }
  }

  const openModal = (entityType, mode = 'create', initialData = null, event = null) => {
    setModalConfig({ entityType, mode, initialData })
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    } else if (fabRef.current) {
      setOriginRect(fabRef.current.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  const handleSave = async (data) => {
    const ops = getOperationsForType(modalConfig.entityType)
    try {
      if (modalConfig.mode === 'create') {
        await ops.add(data)
        console.log('Created:', modalConfig.entityType, data)
      } else {
        await ops.update(modalConfig.initialData.id, data)
        console.log('Updated:', modalConfig.entityType, data)
      }
      setModalOpen(false)
    } catch (err) {
      console.error('Save failed:', err)
      throw err
    }
  }

  const handleDelete = async () => {
    const ops = getOperationsForType(modalConfig.entityType)
    try {
      await ops.remove(modalConfig.initialData.id)
      console.log('Deleted:', modalConfig.entityType)
      setModalOpen(false)
    } catch (err) {
      console.error('Delete failed:', err)
      throw err
    }
  }

  return (
    <motion.div 
      className="design-demo"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <h1 className="design-demo__title">
        <span className="text-gradient-mixed">Design System</span>
      </h1>
      <p className="design-demo__subtitle">{BRANDING.name} Component Library</p>

      {/* Entity Modal Demo */}
      <GlassPanel glow="cyan" padding="lg" className="design-demo__section">
        <h3>Entity Modal</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
          Click buttons below to open create/edit modals for different entity types
        </p>
        <div className="design-demo__row">
          <Button 
            variant="primary" 
            leftIcon={<Icon name="node-diamond" size={14} />}
            onClick={(e) => openModal('character', 'create', null, e)}
          >
            New Character
          </Button>
          <Button 
            variant="secondary" 
            leftIcon={<Icon name="node-diamond" size={14} />}
            onClick={(e) => openModal('location', 'create', null, e)}
          >
            New Location
          </Button>
          <Button 
            variant="outline" 
            leftIcon={<Icon name="node-circle" size={14} />}
            onClick={(e) => openModal('event', 'create', null, e)}
          >
            New Event
          </Button>
          <Button 
            variant="ghost" 
            leftIcon={<Icon name="node-diamond" size={14} />}
            onClick={(e) => openModal('shot', 'create', null, e)}
          >
            New Shot
          </Button>
        </div>
      </GlassPanel>

      {/* Buttons Section */}
      <GlassPanel glow="cyan" padding="lg" className="design-demo__section">
        <h3>Buttons</h3>
        <div className="design-demo__row">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
        </div>
        <div className="design-demo__row">
          <Button variant="primary" size="sm">Small</Button>
          <Button variant="primary" size="md">Medium</Button>
          <Button variant="primary" size="lg">Large</Button>
        </div>
        <div className="design-demo__row">
          <Button variant="primary" leftIcon={<Icon name="plus" size={16} />}>With Icon</Button>
          <Button variant="secondary" rightIcon={<Icon name="arrow-right" size={16} />}>Next Step</Button>
          <Button variant="outline" loading>Loading</Button>
          <Button variant="ghost" disabled>Disabled</Button>
        </div>
      </GlassPanel>

      {/* Glass Panels Section */}
      <div className="design-demo__panels">
        <GlassPanel hoverable padding="md">
          <h4>Default Panel</h4>
          <p>Frosted glass effect with subtle border</p>
        </GlassPanel>
        <GlassPanel glow="cyan" hoverable padding="md">
          <h4>Cyan Glow</h4>
          <p>Electric cyan accent glow effect</p>
        </GlassPanel>
        <GlassPanel glow="amber" hoverable padding="md">
          <h4>Amber Glow</h4>
          <p>Warm amber accent glow effect</p>
        </GlassPanel>
      </div>

      {/* FAB Section */}
      <GlassPanel variant="elevated" padding="lg" className="design-demo__section">
        <h3>Floating Action Buttons</h3>
        <div className="design-demo__row">
          <FloatingActionButton icon={<Icon name="plus" size={16} />} variant="primary" size="sm" label="Add" />
          <FloatingActionButton icon={<Icon name="plus" size={20} />} variant="primary" size="md" label="Add" />
          <FloatingActionButton icon={<Icon name="plus" size={24} />} variant="primary" size="lg" label="Add" />
        </div>
        <div className="design-demo__row">
          <FloatingActionButton icon={<Icon name="edit" size={16} />} variant="secondary" size="sm" label="Edit" />
          <FloatingActionButton icon={<Icon name="edit" size={20} />} variant="secondary" size="md" label="Edit" />
          <FloatingActionButton icon={<Icon name="edit" size={24} />} variant="secondary" size="lg" label="Edit" />
        </div>
      </GlassPanel>

      {/* Entity Cards Section */}
      <GlassPanel padding="lg" className="design-demo__section">
        <h3>Entity Cards</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
          Click cards to open edit modal (demo with sample data)
        </p>
        <div className="design-demo__cards">
          <EntityCard
            type="character"
            name="Elena Torres"
            subtitle="Lead Protagonist"
            details={[
              { label: 'Era', value: 'Contemporary' },
              { label: 'Role', value: 'Lead' },
            ]}
            index={0}
            onClick={(e) => openModal('character', 'edit', {
              id: 'demo-1',
              name: 'Elena Torres',
              description: 'A determined investigator uncovering hidden truths in her city.',
              attire: 'Modern professional wear with a signature jacket',
              hairStyle: 'Dark hair, usually tied back',
            }, e)}
          />
          <EntityCard
            type="location"
            name="Central Station"
            subtitle="Urban transit hub"
            details={[
              { label: 'Era', value: 'Present Day' },
              { label: 'Type', value: 'Interior/Exterior' },
            ]}
            index={1}
            onClick={(e) => openModal('location', 'edit', {
              id: 'demo-2',
              name: 'Central Station',
              description: 'A bustling metropolitan transit hub where paths cross.',
              era: 'Present Day',
              keyElements: 'Grand clock, arched ceilings, busy platforms',
            }, e)}
          />
          <EntityCard
            type="event"
            name="The Turning Point"
            subtitle="Pivotal story moment"
            details={[
              { label: 'Date', value: 'Act II' },
              { label: 'Layer', value: 'A' },
            ]}
            index={2}
            onClick={(e) => openModal('event', 'edit', {
              id: 'demo-3',
              title: 'The Turning Point',
              description: 'The protagonist discovers a crucial piece of information that changes everything.',
              date: 'Act II',
              era: 'Present',
              showOnTimeline: true,
            }, e)}
          />
          <EntityCard
            type="shot"
            name="SC01_SH01_A"
            subtitle="Opening wide shot"
            details={[
              { label: 'Duration', value: '4.2s' },
              { label: 'Frames', value: '001-126' },
            ]}
            index={3}
            onClick={(e) => openModal('shot', 'edit', {
              id: 'demo-4',
              title: 'Opening Wide Shot',
              shotId: 'SC01_SH01_A',
              description: 'Wide establishing shot of the city skyline at dusk.',
              durationSeconds: 4.2,
              layer: 'A',
              aspectRatio: '16:9',
            }, e)}
          />
        </div>
      </GlassPanel>

      {/* Color Palette */}
      <GlassPanel padding="lg" className="design-demo__section">
        <h3>Color Palette</h3>
        <div className="design-demo__colors">
          <div className="color-swatch color-swatch--cyan">
            <span>Cyan</span>
            <code>#00f0ff</code>
          </div>
          <div className="color-swatch color-swatch--amber">
            <span>Amber</span>
            <code>#ff9500</code>
          </div>
          <div className="color-swatch color-swatch--bg">
            <span>Background</span>
            <code>#0a0a0f</code>
          </div>
        </div>
      </GlassPanel>

      {/* The actual FAB that opens character modal */}
      <div ref={fabRef}>
        <FloatingActionButton 
          icon={<Icon name="plus" size={24} />} 
          variant="primary" 
          size="lg" 
          position="bottom-right"
          label="Add new character"
          onClick={(e) => openModal('character', 'create', null, e)}
        />
      </div>

      {/* Entity Modal */}
      <EntityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        entityType={modalConfig.entityType}
        mode={modalConfig.mode}
        initialData={modalConfig.initialData}
        onSave={handleSave}
        onDelete={modalConfig.mode === 'edit' ? handleDelete : undefined}
        originRect={originRect}
      />
    </motion.div>
  )
}

// Placeholder views - will be implemented in later todos
const PlaceholderView = ({ title, icon }) => (
  <motion.div 
    className="placeholder-view"
    variants={pageVariants}
    initial="initial"
    animate="animate"
    exit="exit"
  >
    <div className="placeholder-icon">{icon}</div>
    <h2>{title}</h2>
    <p>Coming soon...</p>
  </motion.div>
)

// TimelineView is now imported from ./views/TimelineView
// CharactersView is now imported from ./views/CharactersView
// LocationsView is now imported from ./views/LocationsView
// EventsView is now imported from ./views/EventsView
// ShotsView is now imported from ./views/ShotsView
// SynopsisView is now imported from ./views/SynopsisView
// ImagesView is now imported from ./views/ImagesView
// StoryboardView is now imported from ./views/StoryboardView

// ProjectsLayout - Simple wrapper for the projects list view
// Works for both anonymous and logged-in users
function ProjectsLayout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign out error:', err)
    }
  }

  return (
    <div className="app app--with-footer">
      <header className="app-header app-header--minimal">
        <div className="logo">
          <span className="logo-icon"><Icon name={APP_LOGO_ICON_NAME} size={24} /></span>
          <span className="logo-text">{APP_LOGO_TEXT}</span>
        </div>
        <div className="header-actions">
          {user ? (
            <button className="sign-out-btn" onClick={handleSignOut} title="Sign out">
              <span className="nav-icon"><Icon name="logout" size={20} /></span>
              <span className="nav-label">Sign out</span>
            </button>
          ) : (
            <button className="sign-in-btn" onClick={() => navigate('/login')} title="Sign in">
              <span className="nav-icon"><Icon name="login" size={20} /></span>
              <span className="nav-label">Sign in</span>
            </button>
          )}
        </div>
      </header>
      
      <main className="app-main">
        <ProjectsListView />
      </main>

      {/* Public footer with contact info and policy links - visible to Stripe crawlers */}
      <PublicFooter />
    </div>
  )
}

function App() {
  const { loading } = useAuth()

  // Show loading screen while checking auth
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <>
      {/* Onboarding Overlay - renders on top of everything when active */}
      <OnboardingOverlay />
      
      <Routes>
      {/* Root = Real dashboard (projects list) - no login required to view */}
      <Route path="/" element={<ProjectsLayout />} />
      
      {/* Public info pages */}
      <Route path="/about" element={<LandingView />} />
      <Route path="/login" element={<LoginView />} />
      <Route path="/terms" element={<TermsView />} />
      <Route path="/privacy" element={<PrivacyView />} />
      <Route path="/refunds" element={<RefundsView />} />
      <Route path="/cancellation" element={<CancellationView />} />
      
      {/* Projects list (same as root) */}
      <Route path="/projects" element={<ProjectsLayout />} />
      
      {/* Project-scoped routes - no login required to view, actions check auth */}
      <Route path="/projects/:projectId" element={<ProjectLayout />}>
        <Route index element={<TimelineView />} />
        <Route path="characters" element={<CharactersView />} />
        <Route path="locations" element={<LocationsView />} />
        <Route path="events" element={<EventsView />} />
        <Route path="shots" element={<ShotsView />} />
        <Route path="synopsis" element={<SynopsisView />} />
        <Route path="images" element={<ImagesView />} />
        <Route path="prompts" element={<PromptsView />} />
        <Route path="import" element={<ImportView />} />
        <Route path="storyboard" element={<StoryboardView />} />
        <Route path="members" element={<ProjectMembersView />} />
        <Route path="testing" element={<TestingView />} />
        <Route path="payments" element={<PaymentView />} />
        <Route path="design" element={<DesignSystemDemo />} />
      </Route>
      
      {/* Catch-all redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  )
}

export default App
